import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import qs from "query-string";
import Header from "../../component/Header";
import JobPostJobPage from "../../component/JobPostJobPage";
import Card from "./Crad";
import { statesAndLgas } from "../../stateAndLga";
import JobSearchLoader from "../../component/utilities/JobSearchLoader";
import { useFetchData } from "../../hooks/useFtechData";
import { ALL_JOB } from "../../resources/resource";

import { jobLevels, experienceLengths, workTypes } from "../../assets/jobData";

// Optional: swap in real photography per industry. Falls back to a gradient banner
// if an industry isn't in this map, so you can fill it in incrementally.
const industryBannerImages = {
  Hospitality:
    "https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1600&q=80",
};

const stateOptions = Object.keys(statesAndLgas);

function IndustryJobsPage() {
  const { industryName } = useParams();
  const industry = decodeURIComponent(industryName || "");
  const navigate = useNavigate();
  const location = useLocation();

  const { data: allJobsData, loading } = useFetchData(ALL_JOB);

  const [keyword, setKeyword] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedLga, setSelectedLga] = useState("");
  const [workType, setWorkType] = useState("");
  const [jobLevel, setJobLevel] = useState("");
  const [experienceLength, setExperienceLength] = useState("");
  const [minSalary, setMinSalary] = useState("");
  const [maxSalary, setMaxSalary] = useState("");

  // Reset location-dependent filters when the industry itself changes
  useEffect(() => {
    setSelectedState("");
    setSelectedLga("");
  }, [industry]);

  // Base set: every job in this industry
  const industryJobs = useMemo(() => {
    if (!allJobsData) return [];
    return allJobsData.filter(
      (job) => job.industry?.toLowerCase() === industry.toLowerCase()
    );
  }, [allJobsData, industry]);

  const lgaOptions = selectedState ? statesAndLgas[selectedState] || [] : [];

  const filteredJobs = useMemo(() => {
    let results = [...industryJobs];

    if (keyword) {
      results = results.filter((job) =>
        job.jobTitle?.toLowerCase().includes(keyword.toLowerCase())
      );
    }
    if (selectedState) {
      results = results.filter(
        (job) => job.state?.toLowerCase() === selectedState.toLowerCase()
      );
    }
    if (selectedLga) {
      results = results.filter(
        (job) => job.localGovernment?.toLowerCase() === selectedLga.toLowerCase()
      );
    }
    if (workType) {
      results = results.filter(
        (job) => job.workType?.toLowerCase() === workType.toLowerCase()
      );
    }
    if (jobLevel) {
      results = results.filter(
        (job) => job.jobLevel?.toLowerCase() === jobLevel.toLowerCase()
      );
    }
    if (experienceLength) {
      results = results.filter(
        (job) => job.experienceLength?.toLowerCase() === experienceLength.toLowerCase()
      );
    }

    const minSal = parseFloat(minSalary);
    const maxSal = parseFloat(maxSalary);
    if (!isNaN(minSal)) {
      results = results.filter((job) => {
        const s = parseFloat(job.salary);
        return !isNaN(s) && s >= minSal;
      });
    }
    if (!isNaN(maxSal)) {
      results = results.filter((job) => {
        const s = parseFloat(job.salary);
        return !isNaN(s) && s <= maxSal;
      });
    }

    return results;
  }, [
    industryJobs,
    keyword,
    selectedState,
    selectedLga,
    workType,
    jobLevel,
    experienceLength,
    minSalary,
    maxSalary,
  ]);

  // State-level counts, scoped to this industry (for the sidebar)
  const stateCounts = useMemo(() => {
    return industryJobs.reduce((acc, job) => {
      if (job.state) acc[job.state] = (acc[job.state] || 0) + 1;
      return acc;
    }, {});
  }, [industryJobs]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // keyword/state already drive filteredJobs via state; this just syncs the URL
    navigate(
      `/industry-jobs/${encodeURIComponent(industry)}?${qs.stringify({
        keyword,
        state: selectedState,
      })}`
    );
  };

  const handleClearFilters = () => {
    setKeyword("");
    setSelectedState("");
    setSelectedLga("");
    setWorkType("");
    setJobLevel("");
    setExperienceLength("");
    setMinSalary("");
    setMaxSalary("");
  };

  const bannerImage =
    industryBannerImages[industry] ||
    "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80";

  if (loading) return <JobSearchLoader />;

  return (
    <div className="min-h-screen bg-gray-100">
      <Header darkMode={true} />

      {/* ============== BANNER ============== */}
      <div className="relative pt-16 sm:pt-20">
        <div
          className="relative h-[340px] sm:h-[380px] bg-cover bg-center"
          style={{ backgroundImage: `url(${bannerImage})` }}
        >
          <div className="absolute inset-0 bg-black/55" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
            <span className="inline-block w-fit px-3 py-1 mb-4 text-xs font-semibold tracking-wide text-orange-300 bg-orange-500/20 border border-orange-400/40 rounded-full uppercase">
              {industry} Sector
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              {industry} Jobs in Nigeria
            </h1>
            <p className="text-gray-200 max-w-xl mb-8">
              Discover premier {industry.toLowerCase()} opportunities across
              Nigeria. Browse current openings and find the role that fits
              your skills and experience.
            </p>
          </div>

          {/* Search bar overlapping the bottom edge of the banner */}
          <form
            onSubmit={handleSearchSubmit}
            className="absolute left-1/2 -bottom-8 -translate-x-1/2 w-[92%] max-w-4xl bg-white rounded-2xl shadow-lg p-3 flex flex-col sm:flex-row gap-2"
          >
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Job title, keywords"
              className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 text-sm"
            />
            <select
              value={selectedState}
              onChange={(e) => {
                setSelectedState(e.target.value);
                setSelectedLga("");
              }}
              className="px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 text-sm sm:w-48"
            >
              <option value="">State</option>
              {stateOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="px-6 py-3 rounded-xl bg-orange-600 text-white font-medium hover:bg-orange-700 transition-colors"
            >
              Search
            </button>
          </form>
        </div>
      </div>

      {/* ============== FILTERS + RESULTS ============== */}
      <div className="container mx-auto p-4 pt-16">
        <div className="flex flex-col lg:flex-row gap-8 mt-4 px-4 sm:px-10">
          {/* Filter panel */}
          <div className="w-full lg:w-1/3 order-2 lg:order-1 space-y-6">
            <Card className="p-6 bg-white shadow-sm rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">Filters</h3>
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-orange-500 hover:underline"
                >
                  Clear all
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    Local Government
                  </label>
                  <select
                    value={selectedLga}
                    onChange={(e) => setSelectedLga(e.target.value)}
                    disabled={!selectedState}
                    className="w-full p-3 border border-gray-300 rounded-xl text-sm disabled:bg-gray-100 focus:outline-none focus:border-orange-500"
                  >
                    <option value="">
                      {selectedState ? "All LGAs" : "Select a state first"}
                    </option>
                    {lgaOptions.map((lga) => (
                      <option key={lga} value={lga}>
                        {lga}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    Work Type
                  </label>
                  <select
                    value={workType}
                    onChange={(e) => setWorkType(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-orange-500"
                  >
                    <option value="">Any</option>
                    {workTypes.map((w) => (
                      <option key={w} value={w}>
                        {w}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    Job Level
                  </label>
                  <select
                    value={jobLevel}
                    onChange={(e) => setJobLevel(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-orange-500"
                  >
                    <option value="">Any</option>
                    {jobLevels.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    Experience
                  </label>
                  <select
                    value={experienceLength}
                    onChange={(e) => setExperienceLength(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-orange-500"
                  >
                    <option value="">Any</option>
                    {experienceLengths.map((e) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    Salary Range (₦)
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="number"
                      value={minSalary}
                      onChange={(e) => setMinSalary(e.target.value)}
                      placeholder="Min"
                      className="w-full p-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-orange-500"
                    />
                    <input
                      type="number"
                      value={maxSalary}
                      onChange={(e) => setMaxSalary(e.target.value)}
                      placeholder="Max"
                      className="w-full p-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white shadow-sm rounded-lg">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                {industry} Jobs by State
              </h2>
              <button
                onClick={() => {
                  setSelectedState("");
                  setSelectedLga("");
                }}
                className={`w-full flex items-center justify-between p-4 rounded-xl mb-3 transition ${
                  !selectedState
                    ? "bg-orange-50 border border-orange-200"
                    : "bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <span className="text-gray-600">All States</span>
                <span className="font-bold text-xl text-orange-600">
                  {industryJobs.length}
                </span>
              </button>
              <div className="space-y-2">
                {Object.entries(stateCounts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([stateName, count]) => (
                    <button
                      key={stateName}
                      onClick={() => {
                        setSelectedState(stateName);
                        setSelectedLga("");
                      }}
                      className={`w-full flex items-center justify-between p-4 rounded-xl transition ${
                        selectedState === stateName
                          ? "bg-orange-50 border border-orange-200"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <span className="text-gray-700">{stateName}</span>
                      <span className="font-bold text-lg text-orange-600">
                        {count}
                      </span>
                    </button>
                  ))}
              </div>
            </Card>
          </div>

          {/* Job listing */}
          <div className="w-full lg:w-2/3 order-1 lg:order-2">
            <p className="text-gray-600 mb-4">
              <span className="font-semibold text-orange-600">
                {filteredJobs.length}
              </span>{" "}
              {industry.toLowerCase()} jobs found
            </p>

            {filteredJobs.length > 0 ? (
              filteredJobs.map((eachJob) => (
                <JobPostJobPage
                  key={eachJob._id}
                  position={eachJob.jobTitle}
                  companyName={eachJob.employer?.companyName}
                  location={eachJob.state}
                  salary={eachJob.salary}
                  jobType={eachJob.workType}
                  experience={eachJob.experienceLength}
                  uniqueViewers={eachJob.uniqueViewers}
                  duration={eachJob.vacancyDuration}
                  jobId={eachJob._id}
                  industry={eachJob.industry}
                  contactMail={eachJob.contactMail}
                  status="Apply now"
                  localGovernment={eachJob.localGovernment}
                  city={eachJob.city}
                  initialLikeCount={eachJob.likeCount || 0}
                  initialFollowCount={eachJob.followCount || 0}
                  isLiked={false}
                  isFollowing={false}
                  employerId={
                    eachJob.employerId ||
                    eachJob.employer?._id ||
                    eachJob.employer?.id
                  }
                />
              ))
            ) : (
              <p className="text-center text-gray-600 py-16 text-lg">
                No {industry.toLowerCase()} jobs found matching your filters.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default IndustryJobsPage;